import { queryOptions, type QueryClient } from '@tanstack/react-query'
import { api, loginRequest } from '@/api/client'
import type { ApiUser } from '@/api/types'
import { canAccessAdmin } from '@/lib/roles'
import { tokenStore, type TokenPair } from './tokens'

export class ForbiddenError extends Error {
  constructor() {
    super('Your account does not have access to the administration')
    this.name = 'ForbiddenError'
  }
}

export const userQueryOptions = queryOptions({
  queryKey: ['auth', 'user'],
  queryFn: () => api.get<ApiUser>('/auth/user').then((r) => r.data),
  staleTime: 5 * 60_000,
  retry: false,
  meta: { silent: true },
})

export function signOut(queryClient: QueryClient) {
  tokenStore.clear()
  queryClient.clear()
}

async function establish(queryClient: QueryClient, pair: TokenPair): Promise<ApiUser> {
  tokenStore.set(pair)
  queryClient.removeQueries({ queryKey: userQueryOptions.queryKey })
  try {
    const user = await queryClient.fetchQuery(userQueryOptions)
    if (!canAccessAdmin(user.roles)) throw new ForbiddenError()
    return user
  } catch (error) {
    signOut(queryClient)
    throw error
  }
}

export const signInWithPassword = async (
  queryClient: QueryClient,
  username: string,
  password: string,
) => establish(queryClient, await loginRequest(username, password))

export const signInWithTokens = establish

export async function getSessionUser(queryClient: QueryClient): Promise<ApiUser | null> {
  if (!tokenStore.getToken() && !tokenStore.getRefreshToken()) return null
  try {
    const user = await queryClient.ensureQueryData(userQueryOptions)
    if (canAccessAdmin(user.roles)) return user
    signOut(queryClient)
    throw new ForbiddenError()
  } catch (error) {
    if (error instanceof ForbiddenError) throw error
    signOut(queryClient)
    return null
  }
}
